import React, { useState, useEffect, useRef } from "react";
import apiAxios from "../../../../services/apiAxios";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";

const Statistics = () => {
    const [doctors, setDoctors] = useState([]);
    const [selectedDocIds, setSelectedDocIds] = useState([]);
    const [selectedPaymentModes, setSelectedPaymentModes] = useState([]);
    const [showDocDropdown, setShowDocDropdown] = useState(false);
    const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);

    const docDropdownRef = useRef(null);
    const paymentDropdownRef = useRef(null);

    const paymentModesList = [
        { name: "Cash", value: "cash" },
        { name: "Card", value: "card" },
        { name: "UPI", value: "upi" }
    ];

    const [stats, setStats] = useState({
        blocks: {
            weekly: {
                lastToLastWeek: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "Last to Last Wk" },
                lastWeek: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "Last Wk" },
                thisWeek: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "This Wk" },
            },
            monthly: {
                lastToLastMonth: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "Last to Last Mo" },
                lastMonth: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "Last Mo" },
                thisMonth: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "This Mo" },
            },
            yearly: {
                lastYear: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "Last Yr" },
                thisYear: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "This Yr" },
                lifetime: { NetRevenue: 0, GrossRevenue: 0, ExternalFees: 0, Orders: 0, CancelledOrders: 0, label: "Lifetime" },
            },
        },
        trends: {
            weekly: [],
            monthly: [],
            yearly: []
        }
    });

    const [loading, setLoading] = useState(false);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (docDropdownRef.current && !docDropdownRef.current.contains(e.target)) {
                setShowDocDropdown(false);
            }
            if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(e.target)) {
                setShowPaymentDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const payload = {
                doctorIds: selectedDocIds,
                paymentModes: selectedPaymentModes
            };
            const response = await apiAxios.post("api/admin/statistics/data", payload);
            if (response.data) {
                setDoctors(response.data.doctors || []);
                if (response.data.stats) {
                    setStats(response.data.stats);
                }
            }
        } catch (err) {
            console.error("Failed to fetch statistics", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocIds, selectedPaymentModes]);

    const toggleDoc = (did) => {
        setSelectedDocIds((prev) =>
            prev.includes(did) ? prev.filter((id) => id !== did) : [...prev, did]
        );
    };

    const togglePaymentMode = (val) => {
        setSelectedPaymentModes((prev) =>
            prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
        );
    };

    const formatCurrency = (val) => `₹${val.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

    const Block = ({ data }) => (
        <div className="flex flex-col flex-1 p-5 bg-white border border-primary-100 rounded-[12px] shadow-sm hover:shadow-md transition-shadow">
            <h4 className="text-[15px] font-semibold text-secondry-700 mb-3">{data.label}</h4>
            <div className="flex flex-col gap-2 text-[14px] text-secondry-600">
                <div className="flex justify-between items-center border-b border-primary-50 pb-1">
                    <span className="text-primary-400 font-medium">Orders:</span>
                    <span className="font-semibold text-gray-700">{data.Orders || 0}</span>
                </div>
                <div className="flex justify-between items-center border-b border-primary-50 pb-1">
                    <span className="text-primary-400 font-medium">Cancelled Orders:</span>
                    <span className="font-semibold text-gray-700">{data.CancelledOrders || 0}</span>
                </div>
                <div className="flex justify-between items-center border-b border-primary-50 pb-1">
                    <span className="text-primary-400 font-medium">Gross Revenue:</span>
                    <span className="font-semibold text-gray-700">{formatCurrency(data.GrossRevenue || 0)}</span>
                </div>
                <div className="flex justify-between items-center border-b border-primary-50 pb-1">
                    <span className="text-primary-400 font-medium">Net Revenue:</span>
                    <span className="font-semibold text-blue-600">{formatCurrency(data.NetRevenue || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-primary-400 font-medium">Externals:</span>
                    <span className="font-semibold text-gray-700">{formatCurrency(data.ExternalFees || 0)}</span>
                </div>
            </div>
        </div>
    );

    const CustomChart = ({ data, title, keys, type }) => (
        <div className="flex flex-col bg-white border border-primary-100 rounded-[12px] p-5 shadow-sm">
            <h4 className="text-[16px] font-semibold text-secondry-700 mb-6">{title}</h4>
            <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                        <defs>
                            <linearGradient id="color1" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="color2" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="color3" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#E62121" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#E62121" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                        <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#6B7280" }} axisLine={false} tickLine={false} dy={10} />
                        <YAxis tick={{ fontSize: 13, fill: "#6B7280" }} tickFormatter={(val) => `₹${val}`} axisLine={false} tickLine={false} dx={-10} />
                        <Tooltip
                            labelFormatter={(label) => {
                                if (type === "weekly") {
                                    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
                                    return `${label}, ${days[parseInt(label) - 1]}`;
                                } else if (type === "monthly") {
                                    const date = new Date();
                                    date.setDate(parseInt(label));
                                    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                                    return `${label}, ${dayName}`;
                                }
                                return label;
                            }}
                            formatter={(value) => formatCurrency(value)}
                            cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                            contentStyle={{ borderRadius: "10px", border: "1px solid #E5E7EB", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)", padding: "10px" }}
                        />
                        <Legend wrapperStyle={{ fontSize: "13px", paddingTop: "20px" }} iconType="circle" />
                        {keys.length > 0 && <Area type="monotone" name={keys[0].name} dataKey={keys[0].key} stroke="#3B82F6" fillOpacity={1} fill="url(#color1)" />}
                        {keys.length > 1 && <Area type="monotone" name={keys[1].name} dataKey={keys[1].key} stroke="#10B981" fillOpacity={1} fill="url(#color2)" />}
                        {keys.length > 2 && <Area type="monotone" name={keys[2].name} dataKey={keys[2].key} stroke="#E62121" fillOpacity={1} fill="url(#color3)" />}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-6 p-1 bg-transparent rounded-[12px]">
            {/* 1. Selectors Header */}
            <div className="flex flex-col md:flex-row gap-4 w-full relative z-20">
                {/* Doctor Selector */}
                <div className="flex-1 relative" ref={docDropdownRef}>
                    <button
                        type="button"
                        onClick={() => setShowDocDropdown(!showDocDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-primary-200 rounded-[10px] text-[14px] text-secondry-700 hover:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all shadow-sm"
                    >
                        <span className="font-medium">
                            {selectedDocIds.length > 0
                                ? `${selectedDocIds.length} Doctor${selectedDocIds.length > 1 ? "s" : ""} selected`
                                : "Doctors (All)"}
                        </span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{ transform: showDocDropdown ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                        >
                            <path d="M7 10l5 5 5-5z" fill="#666" />
                        </svg>
                    </button>

                    {showDocDropdown && (
                        <div className="absolute z-30 w-full mt-2 bg-white border border-primary-200 rounded-[10px] shadow-xl max-h-[300px] overflow-y-auto">
                            {doctors.map((doc) => (
                                <label
                                    key={doc.DID}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-primary-50 cursor-pointer transition-all border-b border-gray-100 last:border-b-0"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedDocIds.includes(doc.DID)}
                                        onChange={() => toggleDoc(doc.DID)}
                                        className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 accent-primary-600"
                                    />
                                    <span className="flex-1 text-[14px] text-secondry-700 font-medium">
                                        Dr. {doc.name} {doc.surname}
                                    </span>
                                </label>
                            ))}
                            {doctors.length === 0 && (
                                <div className="px-4 py-4 text-sm text-gray-400 text-center italic">No doctors found ...</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Payment Mode Selector */}
                <div className="flex-1 relative" ref={paymentDropdownRef}>
                    <button
                        type="button"
                        onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-primary-200 rounded-[10px] text-[14px] text-secondry-700 hover:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none transition-all shadow-sm"
                    >
                        <span className="font-medium">
                            {selectedPaymentModes.length > 0
                                ? `${selectedPaymentModes.length} Method${selectedPaymentModes.length > 1 ? "s" : ""} selected`
                                : "Payment Method (All)"}
                        </span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{ transform: showPaymentDropdown ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                        >
                            <path d="M7 10l5 5 5-5z" fill="#666" />
                        </svg>
                    </button>

                    {showPaymentDropdown && (
                        <div className="absolute z-30 w-full mt-2 bg-white border border-primary-200 rounded-[10px] shadow-xl overflow-hidden">
                            {paymentModesList.map((mode) => (
                                <label
                                    key={mode.value}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-primary-50 cursor-pointer transition-all border-b border-gray-100 last:border-b-0"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedPaymentModes.includes(mode.value)}
                                        onChange={() => togglePaymentMode(mode.value)}
                                        className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 accent-primary-600"
                                    />
                                    <span className="flex-1 text-[14px] text-secondry-700 font-medium uppercase">
                                        {mode.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className={`transition-opacity duration-300 ${loading ? "opacity-40" : "opacity-100"}`}>
                {/* 2. Revenue Blocks */}
                <div className="flex flex-col gap-5 mb-8">
                    {/* Row 1: Weekly */}
                    <div className="flex flex-col md:flex-row gap-5">
                        <Block data={stats.blocks.weekly.lastToLastWeek} />
                        <Block data={stats.blocks.weekly.lastWeek} />
                        <Block data={stats.blocks.weekly.thisWeek} />
                    </div>

                    {/* Row 2: Monthly */}
                    <div className="flex flex-col md:flex-row gap-5">
                        <Block data={stats.blocks.monthly.lastToLastMonth} />
                        <Block data={stats.blocks.monthly.lastMonth} />
                        <Block data={stats.blocks.monthly.thisMonth} />
                    </div>

                    {/* Row 3: Yearly */}
                    <div className="flex flex-col md:flex-row gap-5">
                        <Block data={stats.blocks.yearly.lastYear} />
                        <Block data={stats.blocks.yearly.thisYear} />
                        <Block data={stats.blocks.yearly.lifetime} />
                    </div>
                </div>

                {/* 3. Revenue Charts */}
                <div className="flex flex-col gap-6">
                    <CustomChart
                        data={stats.trends.weekly}
                        title="Week-wise Revenue Trends"
                        type="weekly"
                        keys={[
                            { key: "LastToLastWeek", name: stats.blocks.weekly.lastToLastWeek.label },
                            { key: "LastWeek", name: stats.blocks.weekly.lastWeek.label },
                            { key: "ThisWeek", name: stats.blocks.weekly.thisWeek.label }
                        ]}
                    />
                    <CustomChart
                        data={stats.trends.monthly}
                        title="Month-wise Revenue Trends"
                        type="monthly"
                        keys={[
                            { key: "LastToLastMonth", name: stats.blocks.monthly.lastToLastMonth.label },
                            { key: "LastMonth", name: stats.blocks.monthly.lastMonth.label },
                            { key: "ThisMonth", name: stats.blocks.monthly.thisMonth.label }
                        ]}
                    />
                    <CustomChart
                        data={stats.trends.yearly}
                        title="Year-wise Revenue Trends"
                        type="yearly"
                        keys={[
                            { key: "LastYear", name: stats.blocks.yearly.lastYear.label },
                            { key: "ThisYear", name: stats.blocks.yearly.thisYear.label }
                        ]}
                    />
                </div>
            </div>
        </div>
    );
};

export default Statistics;
